import { PropsWithChildren, useMemo } from "react";
import { Claims, UserTypes } from "../../models";
import { useAuth, usePermissions, useProfile } from "../../contexts";
import { Navigate, Outlet } from "react-router-dom";

interface ProtectedRouteProps extends PropsWithChildren {
    claim?: Claims;
    anyClaim?: Claims[];
    allClaims?: Claims[];
    role?: UserTypes;
    anyRole?: UserTypes[];
    allRoles?: UserTypes[];
    isAuthenticated?: boolean;
    redirectTo?: string;
    isExternal?: boolean;
}

/**
 * A ProtectedRoute is internal by default, meaning the ViewInternal claim is required unless props.isExternal is set to true.
 */
export function ProtectedRoute(props: Readonly<ProtectedRouteProps>) {
    const { claim, anyClaim, allClaims, role, anyRole, allRoles, isAuthenticated, isExternal, redirectTo, children } = props;
    const { hasPermission, hasRole } = usePermissions();
    const { isAuthenticated: isAuth, isRefreshing } = useAuth();
    const { userIsLoading } = useProfile();

    const isAuthorized = useMemo(() => {
        if (!isExternal && !hasPermission(Claims.ViewInternal)) return false;
        if (claim) return hasPermission(claim);
        if (role) return hasRole(role);
        if (anyClaim) return anyClaim.some((c) => hasPermission(c));
        if (allClaims) return allClaims.every((c) => hasPermission(c));
        if (anyRole) return anyRole.some((r) => hasRole(r));
        if (allRoles) return allRoles.every((r) => hasRole(r));
        if (isAuthenticated !== undefined) return isAuthenticated === isAuth;
        return true;
    }, [
        allClaims,
        allRoles,
        anyClaim,
        anyRole,
        claim,
        hasPermission,
        hasRole,
        isAuth,
        isAuthenticated,
        role,
        isExternal,
    ]);

    if (!isAuthorized && !isRefreshing && !userIsLoading) {
        return <Navigate to={redirectTo ?? "/"} replace />;
    } else if (isAuthorized) {
        return children ? <>{children}</> : <Outlet />;
    }
    return null;
}
